import { Platform } from 'react-native';

/**
 * Simple utility to manage document title for web platform
 * Sets a consistent title for the entire website without flickering
 */
class DocumentTitleManager {
  private static instance: DocumentTitleManager;
  private readonly APP_TITLE = 'GenioMD - Your Personal Medical Assistant';
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): DocumentTitleManager {
    if (!DocumentTitleManager.instance) {
      DocumentTitleManager.instance = new DocumentTitleManager();
    }
    return DocumentTitleManager.instance;
  }

  /**
   * Initialize the document title for web platform
   * This should be called once when the app starts
   */
  public initialize(): void {
    if (Platform.OS === 'web' && !this.isInitialized) {
      // Set the initial title
      document.title = this.APP_TITLE;
      
      // Override any future title changes to maintain consistency
      this.overrideTitleChanges();
      
      this.isInitialized = true;
    }
  }

  /**
   * Override any attempts to change the document title
   * This ensures the title remains consistent across all screens
   */
  private overrideTitleChanges(): void {
    if (Platform.OS !== 'web') return;

    // Store the original title property descriptor
    const originalDescriptor = Object.getOwnPropertyDescriptor(Document.prototype, 'title') || 
                              Object.getOwnPropertyDescriptor(HTMLDocument.prototype, 'title');

    if (originalDescriptor) {
      // Override the title setter to always use our app title
      Object.defineProperty(document, 'title', {
        get: () => this.APP_TITLE,
        set: () => {
          // Do nothing - title remains constant
        },
        configurable: true,
        enumerable: true
      });
    }

    // Also override any direct manipulation of the title element
    const titleElement = document.querySelector('title');
    if (titleElement) {
      titleElement.textContent = this.APP_TITLE;
      
      // Create a MutationObserver to prevent title changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            titleElement.textContent = this.APP_TITLE;
          }
        });
      });
      
      observer.observe(titleElement, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
  }

  /**
   * Get the current app title
   */
  public getTitle(): string {
    return this.APP_TITLE;
  }

  /**
   * Reset the title (useful for testing or if needed)
   */
  public reset(): void {
    if (Platform.OS === 'web') {
      this.isInitialized = false;
      this.initialize();
    }
  }
}

// Export a singleton instance
export default DocumentTitleManager.getInstance();
